import { Box, Flex, Heading } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { MissedDetectionDetails } from 'components/sections/admin-portal/machines/dialogs/settings/machine-shot-history/missed-vision-detections/missed-detection-details';
import { MissedDetectionsHistoryTable } from 'components/sections/admin-portal/machines/dialogs/settings/machine-shot-history/missed-vision-detections/missed-detections-history-table';
import { HistoryTabSelector } from 'components/sections/admin-portal/machines/dialogs/settings/machine-shot-history/tab-selector';
import { useMachineMissedDetectionsDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineMissedDetectionsDialogState';
import { useMachineShotHistoryDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineShotHistoryDialogState';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { MACHINE_SETTINGS_CONTENT_ID } from 'enums/admin-portal';
import { HistoryTab } from 'enums/machine-settings';
import useLatest from 'hooks/useLatestRef';
import usePrevious from 'hooks/usePrevious';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMissedDetectionsHistoryWithTotal } from 'lib_ts/interfaces/data/i-machine-shot-history';
import { IMachineDetailsShotMsg } from 'lib_ts/interfaces/machine-msg/i-machine-details';
import {
  IMissedVisionDetection,
  VisionDetectionType,
} from 'lib_ts/interfaces/vision/i-vision-detection';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AdminVisionDetectionsService } from 'services/admin/vision-detections';
import { useAuthStore } from 'stores/use-auth-store';

const RECORDS_PER_REQUEST = 25;
const MIN_PITCH_NAME_QUERY_LENGTH = 2;
const MIN_MESSAGE_QUERY_LENGTH = 2;

export const MissedVisionDetections = () => {
  const wsConnected = useAuthStore((s) => s.wsConnected);
  const machine = useMachinesPortalStore((s) => s.machine);

  const { historyTab } = useMachineShotHistoryDialogState();

  const {
    missedDetectionID,
    missedDetectionFilters,
    setMissedDetectionID,
    resetMissedDetectionID,
  } = useMachineMissedDetectionsDialogState();

  const initializedRef = useRef(false);
  const prevFilters = usePrevious(missedDetectionFilters);

  const [loading, setLoading] = useState(false);

  // total number of detections matching the missedDetectionFilters, as provided by the server
  const [total, setTotal] = useState(0);
  const [missedDetections, setMissedDetections] = useState<
    IMissedVisionDetection[]
  >([]);

  // ID of detections that were recently added/updated via WS for row highlighting
  // todo: should we queue these up to highlight them when the table comes back into view after drill down?
  const [newMissedDetectionID, setNewMissedDetectionID] = useState<string>('');
  const [updatedMissedDetectionID, setUpdatedMissedDetectionID] =
    useState<string>('');

  // show MissedDetectionDetails when a missed detection is selected (missed detection ID stored in URL params via setMissedDetectionID)
  const [selectedMissedDetection, setSelectedMissedDetection] =
    useState<IMissedVisionDetection>();
  const latestSelectedMissedDetection = useLatest(selectedMissedDetection); // stored in ref to avoid adding to WS useEffect deps

  const scrollPositionRef = useRef<number>();

  const loadData = useCallback(
    async (
      skip: number,
      silently?: boolean
    ): Promise<IMissedDetectionsHistoryWithTotal | undefined> => {
      if (!machine?.machineID) {
        return;
      }

      try {
        if (!silently) {
          setLoading(true);
        }

        const coercedDateRange = ():
          | { start_date: string; end_date: string }
          | undefined => {
          if (!missedDetectionFilters.dateRange) {
            return undefined;
          }

          return {
            start_date: missedDetectionFilters.dateRange.start.toISOString(),
            end_date: missedDetectionFilters.dateRange.end.toISOString(),
          };
        };

        const coercedPitchName = () => {
          const sanitizedPitchName = missedDetectionFilters.pitchName.trim();
          return sanitizedPitchName.length < MIN_PITCH_NAME_QUERY_LENGTH
            ? undefined
            : sanitizedPitchName;
        };

        const coercedMessage = () => {
          const sanitizedMessage = missedDetectionFilters.message.trim();
          return sanitizedMessage.length < MIN_MESSAGE_QUERY_LENGTH
            ? undefined
            : sanitizedMessage;
        };

        const dates = coercedDateRange();

        const response =
          await AdminVisionDetectionsService.getInstance().getMissedDetectionsHistory(
            {
              machineID: machine.machineID,
              pitch_name: coercedPitchName(),
              message: coercedMessage(),
              start_date: dates?.start_date,
              end_date: dates?.end_date,
              limit: RECORDS_PER_REQUEST,
              with_total: true,
              skip: skip,
            }
          );

        const { total: dbTotal, data: latest } = response;

        setTotal(dbTotal);

        setNewMissedDetectionID('');
        setUpdatedMissedDetectionID('');

        if (skip > 0) {
          setMissedDetections((prev) => [...prev, ...latest]);
          return response;
        }

        setMissedDetections(latest);
        return response;
      } catch (e) {
        NotifyHelper.error({
          message_md: `Unable to load missed detection history: ${e}`,
        });
      } finally {
        // react update batching causing some annoying flickering
        setTimeout(() => {
          setLoading(false);
        }, 0);
      }
    },
    [
      // deeper comparison of missedDetectionFilters for efficiency
      missedDetectionFilters.dateRange,
      missedDetectionFilters.pitchName,
      missedDetectionFilters.message,
      machine?.machineID,
    ]
  );

  useEffect(() => {
    if (!machine?.machineID || initializedRef.current) {
      return;
    }

    const init = async () => {
      setLoading(true);
      await loadData(0, false);
      setLoading(false);
    };

    init();
    initializedRef.current = true;
  }, [machine?.machineID, loadData]);

  const hasFilterSet = useMemo(
    () => Object.values(missedDetectionFilters).some((v) => !!v),
    [missedDetectionFilters]
  );

  // real-time update for new detections
  useEffect(() => {
    if (!wsConnected) {
      return;
    }

    const upsertMissedDetection = async (event: CustomEvent) => {
      const data = event.detail as IMachineDetailsShotMsg;

      if (!data.missedDetection) {
        return;
      }

      // just in case
      if (data.missedDetection?.machineID !== machine?.machineID) {
        return;
      }

      // race condition handling - just in case we get the WS message after we load via REST
      setMissedDetections((prev) => {
        if (!data.missedDetection) {
          return prev;
        }

        const index = prev.findIndex(
          (s) => s._id === data.missedDetection?._id
        );

        if (data.operation === 'update' && index >= 0) {
          const newMissedDetections = [...prev];
          newMissedDetections[index] = data.missedDetection;
          return newMissedDetections;
        }

        return [data.missedDetection, ...prev];
      });

      if (data.operation === 'create') {
        setTotal((prev) => prev + 1);
        setNewMissedDetectionID(data.missedDetection._id);
        setUpdatedMissedDetectionID('');
        return;
      }

      // handlers below are for 'update' operation
      setUpdatedMissedDetectionID(data.missedDetection._id);
      setNewMissedDetectionID('');

      // update the missed detection shown in MissedDetectionDetails if it's the one currently selected
      if (
        data.missedDetection._id === latestSelectedMissedDetection.current?._id
      ) {
        setSelectedMissedDetection(data.missedDetection);
      }
    };

    const unsubscribe = () => {
      WebSocketHelper.remove(
        WsMsgType.S2U_MachineShotHistory,
        upsertMissedDetection
      );
    };

    // don't upsert rows if we're looking at filtered data
    if (hasFilterSet) {
      return unsubscribe;
    }

    WebSocketHelper.on(WsMsgType.S2U_MachineShotHistory, upsertMissedDetection);
    return unsubscribe;
  }, [machine?.machineID, hasFilterSet, wsConnected]);

  useEffect(() => {
    if (!initializedRef.current) {
      return;
    }

    // queries can be expensive, so we'll do a manual equality check here just in case
    // todo: is this actually necessary?
    if (!prevFilters || isEqual(prevFilters, missedDetectionFilters)) {
      return;
    }

    loadData(0);
  }, [missedDetectionFilters, loadData]);

  const onSelect = (v?: IMissedVisionDetection) => {
    if (!v) {
      return;
    }

    // save our scroll position so we can restore it later when the user goes back to the table
    const contentEl = document.getElementById(MACHINE_SETTINGS_CONTENT_ID);
    scrollPositionRef.current = contentEl?.scrollTop;

    setSelectedMissedDetection(v);

    // sync up URL param with selected detection
    setMissedDetectionID(v._id);
  };

  const onBack = () => {
    setSelectedMissedDetection(undefined);

    // remove detectionID from URL params
    resetMissedDetectionID();
  };

  // primarily used when the user is visiting a direct link to an missed detection from outside the app
  useEffect(() => {
    // if we already have the missed detection selected, no need to fetch it again
    if (missedDetectionID === selectedMissedDetection?._id) {
      return;
    }

    if (!missedDetectionID) {
      setSelectedMissedDetection(undefined);
      return;
    }

    const missedDetection = missedDetections.find(
      (s) => s._id === missedDetectionID
    );

    if (missedDetection) {
      setSelectedMissedDetection(missedDetection);
      return;
    }

    const lazyLoadMissedDetection = async () => {
      const result =
        await AdminVisionDetectionsService.getInstance().getById(
          missedDetectionID
        );

      if (!result || result.type !== VisionDetectionType.Missed) {
        NotifyHelper.warning({
          message_md: `No missed detection found with ID ${missedDetectionID}`,
        });

        resetMissedDetectionID();
        return;
      }

      setSelectedMissedDetection(result);
    };

    // if we don't have the shot in our current data set, fetch it from the server
    lazyLoadMissedDetection();
  }, [missedDetectionID, selectedMissedDetection?._id, missedDetections]);

  const selectedDetectionHasPrev = useMemo(() => {
    if (!selectedMissedDetection) {
      return false;
    }

    const currentIndex = missedDetections.findIndex(
      (s) => s._id === selectedMissedDetection._id
    );

    if (currentIndex < 0) {
      return false;
    }

    return currentIndex < total - 1;
  }, [selectedMissedDetection, missedDetections, total]);

  const selectedDetectionHasNext = useMemo(() => {
    if (!selectedMissedDetection) {
      return false;
    }

    const currentIndex = missedDetections.findIndex(
      (s) => s._id === selectedMissedDetection._id
    );

    if (currentIndex < 0) {
      return false;
    }

    return currentIndex > 0;
  }, [selectedMissedDetection, missedDetections]);

  const onNext = () => {
    if (!selectedMissedDetection) {
      return;
    }

    const currentIndex = missedDetections.findIndex(
      (s) => s._id === selectedMissedDetection._id
    );
    const nextIndex = currentIndex - 1;

    if (nextIndex >= 0) {
      const nextDetection = missedDetections[nextIndex];
      setSelectedMissedDetection(nextDetection);
      setMissedDetectionID(nextDetection._id);
    }
  };

  const onPrev = async () => {
    if (!selectedMissedDetection) {
      return;
    }

    const currentIndex = missedDetections.findIndex(
      (s) => s._id === selectedMissedDetection._id
    );
    const prevIndex = currentIndex + 1;

    const loadMore = async () => {
      const newData = await loadData(missedDetections.length, false);
      if (!newData) {
        return;
      }

      const prevDetection = newData.data[0];
      setSelectedMissedDetection(prevDetection);
      setMissedDetectionID(prevDetection._id);
    };

    const isLastDetection = prevIndex === missedDetections.length - 1;
    const hasMoreDetections = prevIndex < total - 1;

    // if we're at the end of our current list, but there are more detections available
    if (isLastDetection && hasMoreDetections) {
      await loadMore();
      return;
    }

    if (prevIndex < missedDetections.length) {
      const prevDetection = missedDetections[prevIndex];
      setSelectedMissedDetection(prevDetection);
      setMissedDetectionID(prevDetection._id);

      return;
    }
  };

  const onLoadMore = () => loadData(missedDetections.length, true);
  const hasMore = missedDetections.length < total;

  const showSection = historyTab === HistoryTab.MissedDetections;
  const showSelector = machine?.enable_trajekt_vision && showSection && (
    <HistoryTabSelector />
  );

  return (
    <ErrorBoundary identifier="MissedDetectionsHistory">
      <Flex
        className={StringHelper.classNames([
          RADIX.VFLEX.GROW,
          showSection ? '' : 'visibility-hidden position-absolute',
        ])}
        direction="column"
      >
        {selectedMissedDetection && (
          <Box>
            <MissedDetectionDetails
              missedDetection={selectedMissedDetection}
              hasPrev={selectedDetectionHasPrev}
              hasNext={selectedDetectionHasNext}
              loadingPrev={loading}
              onBack={onBack}
              onPrev={onPrev}
              onNext={onNext}
            />
          </Box>
        )}
        <Flex
          className={StringHelper.classNames([
            RADIX.VFLEX.GROW,
            /* always keep table in the DOM to persist input states */
            selectedMissedDetection ? 'visibility-hidden' : '',
          ])}
          direction="column"
          gap={RADIX.FLEX.GAP.SM}
        >
          <Flex justify="between" align="center">
            <Heading size="4">Missed Detections</Heading>
            {showSelector && <HistoryTabSelector />}
          </Flex>
          <MissedDetectionsHistoryTable
            missedDetections={missedDetections}
            loading={loading}
            hasMore={hasMore}
            newMissedDetectionID={newMissedDetectionID}
            updatedMissedDetectionID={updatedMissedDetectionID}
            onLoadMore={onLoadMore}
            onSelect={onSelect}
          />
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};
